import { Card, CardActionArea, CardContent, Chip, Grid, Stack, Typography } from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import ExtensionRounded from '@mui/icons-material/ExtensionRounded';
import RedeemRounded from '@mui/icons-material/RedeemRounded';
import SettingsRounded from '@mui/icons-material/SettingsRounded';
import WorkspacePremiumRounded from '@mui/icons-material/WorkspacePremiumRounded';
import WidgetsOutlined from '@mui/icons-material/WidgetsOutlined';
import { Link as RouterLink } from 'react-router';
import { EmptyState } from '@/components/EmptyState';
import { IconBadge } from '@/components/IconBadge';
import { TENANT_SETTINGS_PERMISSIONS } from '@/layouts/navigation';
import { CouponPrompt } from '@/referrals/CouponPrompt';
import { MODULES } from '@/modules/registry';
import { allows, hasModule, isStaff } from '@/session/access';
import { useMe } from '@/session/useSession';
import { useTenant } from '@/tenant/useTenant';
import { PageHeader } from './PageHeader';

const ROLE_LABEL: Record<string, string> = {
  tenant_admin: 'Administração',
  professional: 'Profissional',
  end_user: 'Cliente',
};

/** Tenant home: the contracted modules this person can use, nothing else. */
export function HomePage() {
  const me = useMe();
  const { config, displayName } = useTenant();
  const firstName = me.user.name?.split(' ')[0] ?? 'por aqui';
  const modules = MODULES.filter(
    (module) => hasModule(me, module.key) && module.nav.some((item) => allows(me, item.rule)),
  );
  const staff = isStaff(me);
  const toTurnOn = staff
    ? MODULES.filter(
        (module) => me.entitlements.personal.includes(module.key) && !hasModule(me, module.key),
      )
    : [];
  const offerPlus = !staff && me.entitlements.personalPlans.length === 0;
  const canConfigure = allows(me, { anyPermission: TENANT_SETTINGS_PERMISSIONS });

  return (
    <>
      <PageHeader
        title={`Olá, ${firstName}`}
        subtitle={
          staff
            ? `Este é o painel de ${displayName}.`
            : (config.branding?.tagline ?? `Bem-vindo(a) a ${displayName}.`)
        }
        actions={
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {me.roles.map((role) => (
              <Chip key={role} size="small" variant="soft" label={ROLE_LABEL[role] ?? role} />
            ))}
          </Stack>
        }
      />

      <ReferralBanner staff={staff} />
      <CouponPrompt />

      {modules.length === 0 ? (
        <Card>
          <EmptyState
            icon={<WidgetsOutlined />}
            title="Nenhum módulo disponível ainda"
            description={
              canConfigure
                ? 'Este espaço ainda não tem módulos ativos no plano. Veja o plano em Configurações.'
                : 'Seu acesso está pronto; os módulos aparecerão aqui assim que forem ativados.'
            }
          />
        </Card>
      ) : (
        <Grid container spacing={2}>
          {modules.map((module) => {
            const entry = module.nav.find((item) => allows(me, item.rule))!;
            return (
              <Grid key={module.key} size={{ xs: 12, sm: 6, md: 4 }}>
                <ModuleCard
                  to={entry.to}
                  icon={<module.icon />}
                  title={module.label}
                  description={module.description}
                />
              </Grid>
            );
          })}
          {hasModule(me, 'ai') && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <IconBadge icon={<AutoAwesomeRounded />} tone="accent" />
                    <Typography variant="h6" component="h2">
                      Recursos inteligentes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      A IA está disponível dentro dos módulos deste espaço, sempre como sugestão
                      para você revisar.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
          {toTurnOn.length > 0 && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <ModuleCard
                to="/modulos"
                icon={<ExtensionRounded />}
                title="Mais módulos"
                description={`Ative ${toTurnOn.map((m) => m.label).join(', ')} quando quiser, sem custo.`}
              />
            </Grid>
          )}
          {offerPlus && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <ModuleCard
                to="/plus"
                icon={<WorkspacePremiumRounded />}
                title="Plus"
                description="Finanças pessoais e fotos de refeição analisadas por IA, em todos os seus espaços."
              />
            </Grid>
          )}
          {canConfigure && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <ModuleCard
                to="/configuracoes"
                icon={<SettingsRounded />}
                title="Configurações"
                description="Marca, membros, plano e auditoria do espaço."
              />
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
}

/** Indique e ganhe, highlighted on everyone's home. */
function ReferralBanner({ staff }: { staff: boolean }) {
  return (
    <Card
      sx={{
        mb: 2,
        color: 'primary.contrastText',
        background: (t) =>
          `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
      }}
    >
      <CardActionArea component={RouterLink} to="/indique">
        <CardContent>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <RedeemRounded sx={{ fontSize: 40 }} />
            <Stack sx={{ flexGrow: 1 }}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>
                Ganhe R$ 10 por indicação
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.92 }}>
                {staff
                  ? 'Cada aluno ou profissional que você trouxer e que assinar um plano rende R$ 10 no seu Pix. Sem limite.'
                  : 'Cada pessoa que você trouxer e que assinar um plano rende R$ 10 no seu Pix. Sem limite.'}
              </Typography>
            </Stack>
            <ArrowForwardRounded />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function ModuleCard({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Card sx={{ height: '100%', '&:hover': { borderColor: 'primary.light' } }}>
      <CardActionArea component={RouterLink} to={to} sx={{ height: '100%' }}>
        <CardContent>
          <Stack spacing={1.5}>
            <IconBadge icon={icon} />
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" component="h2">
                {title}
              </Typography>
              <ArrowForwardRounded color="action" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
